/**
 * Optimierter Filter-Store für die Restaurantsuche
 * Einfach, performant und zuverlässig
 */
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

export type DeliveryMode = 'delivery' | 'pickup';

export interface FilterState {
  // Suchparameter
  query: string;
  searchQuery: string; // Alias für query für Kompatibilität
  location: {
    label: string;
    lat?: number;
    lng?: number;
    placeId?: string;
    source?: 'google' | 'photon' | 'nominatim' | 'manual' | 'geolocate' | 'history' | 'map';
  } | null;
  
  // Filter
  cuisineIds: number[];
  distance: number;
  skills: number[];
  openNow: boolean;
  deliveryOnly: boolean;
  deliveryMode: DeliveryMode; // Primary mode: Lieferung or Abholung
  priceLevel: string | null; // €, €€, €€€, €€€€
  
  // Sortierung
  sortOption: string;
  
  // Aktionen
  setQuery: (query: string) => void;
  setSearchQuery: (query: string) => void; // Alias für setQuery
  setLocation: (location: FilterState['location'] | string | null) => void;
  setLocationLabel: (label: string) => void;
  setCoordinates: (lat?: number, lng?: number) => void;
  setCuisines: (cuisineIds: number[]) => void;
  setDistance: (distance: number) => void;
  setSkills: (skills: number[]) => void;
  setOpenNow: (openNow: boolean) => void;
  setDeliveryOnly: (deliveryOnly: boolean) => void;
  setDeliveryMode: (mode: DeliveryMode) => void;
  setPriceLevel: (priceLevel: string | null) => void;
  setSortOption: (sortOption: string) => void;
  resetFilters: () => void;
  resetAll: () => void;
  
  // Hilfsfunktionen
  hasActiveFilters: () => boolean;
  getActiveFiltersCount: () => number;
}

// P0 Fix: Konstante Baseline für active filter compare (zeitunabhängig)
const DEFAULT_FILTERS_BASELINE = {
  query: '',
  searchQuery: '',
  location: null as FilterState['location'],
  cuisineIds: [],
  distance: 20, // Default 20km
  skills: [],
  openNow: false, // Baseline: false (zeitabhängiger Default wird separat gehandhabt)
  deliveryOnly: false,
  deliveryMode: 'delivery' as DeliveryMode, // Default: Lieferung
  priceLevel: null as string | null,
  sortOption: 'Empfohlen'
};

// P0 Helper: Get Default Filters (Single Source of Truth)
// WICHTIG: openNow ist standardmäßig FALSE - zu viele Restaurants haben keine/fehlerhafte Öffnungszeiten
export const getDefaultFilters = () => {
  return {
    ...DEFAULT_FILTERS_BASELINE,
    openNow: false // Standardmäßig AUS - zeigt alle Restaurants
  };
};

// Standardwerte für Filter
const DEFAULT_FILTERS = getDefaultFilters();

// P0 Helper: Normalize Filters (entfernt leere/undefined, trimmed strings)
export const normalizeFilters = (filters: Partial<FilterState>) => {
  const normalized: Partial<FilterState> = {};
  
  if (filters.query !== undefined) {
    normalized.query = String(filters.query).trim();
    normalized.searchQuery = normalized.query; // Sync
  }
  if (filters.searchQuery !== undefined) {
    normalized.searchQuery = String(filters.searchQuery).trim();
    normalized.query = normalized.searchQuery; // Sync
  }
  if (filters.location !== undefined) {
    if (typeof (filters as any).location === 'string') {
      const label = String((filters as any).location).trim();
      normalized.location = label ? { label } : null;
    } else {
      normalized.location = (filters as any).location;
    }
  }
  if (filters.cuisineIds !== undefined) {
    normalized.cuisineIds = Array.isArray(filters.cuisineIds) ? filters.cuisineIds.filter(id => id != null) : [];
  }
  if (filters.skills !== undefined) {
    normalized.skills = Array.isArray(filters.skills) ? filters.skills.filter(id => id != null) : [];
  }
  if (filters.distance !== undefined) {
    normalized.distance = Number(filters.distance) || getDefaultFilters().distance;
  }
  if (filters.openNow !== undefined) {
    normalized.openNow = Boolean(filters.openNow);
  }
  if (filters.deliveryOnly !== undefined) {
    normalized.deliveryOnly = Boolean(filters.deliveryOnly);
  }
  
  return normalized;
};

// P0 Helper: Get Active Filters (chips + count) - verwendet konstante Baseline
export const getActiveFilters = (filters: FilterState, defaults: typeof DEFAULT_FILTERS_BASELINE = DEFAULT_FILTERS_BASELINE) => {
  const chips: Array<{ key: string; label: string; onRemoveKey: string }> = [];
  let activeCount = 0;

  // Küchen (pro Element)
  filters.cuisineIds.forEach(id => {
    chips.push({ key: `cuisine-${id}`, label: `Küche ${id}`, onRemoveKey: `cuisine-${id}` });
    activeCount++;
  });

  // Skills (pro Element)
  filters.skills.forEach(id => {
    chips.push({ key: `skill-${id}`, label: `Skill ${id}`, onRemoveKey: `skill-${id}` });
    activeCount++;
  });

  // Toggles - P0 Fix: Vergleich mit Baseline (zeitunabhängig)
  if (filters.openNow && !defaults.openNow) {
    chips.push({ key: 'openNow', label: 'Geöffnet', onRemoveKey: 'openNow' });
    activeCount++;
  }
  
  // Legacy: deliveryOnly (deprecated, kept for backwards compatibility)
  if (filters.deliveryOnly) {
    chips.push({ key: 'deliveryOnly', label: 'Lieferung (legacy)', onRemoveKey: 'deliveryOnly' });
    activeCount++;
  }
  
  // DeliveryMode: IMMER anzeigen (Primary Filter)
  // Zeigt aktuellen Modus als Chip, Entfernen setzt auf Default 'delivery'
  if (filters.deliveryMode === 'delivery') {
    chips.push({ key: 'deliveryMode', label: 'Lieferung', onRemoveKey: 'deliveryMode' });
    activeCount++;
  } else if (filters.deliveryMode === 'pickup') {
    chips.push({ key: 'deliveryMode', label: 'Abholung', onRemoveKey: 'deliveryMode' });
    activeCount++;
  }

  // Radius (nur wenn != default)
  if (filters.distance !== defaults.distance) {
    chips.push({ key: 'distance', label: `${filters.distance} km`, onRemoveKey: 'distance' });
    activeCount++;
  }

  // Preisniveau
  if (filters.priceLevel) {
    const priceLabels: Record<string, string> = {
      '€': 'Günstig (€)',
      '€€': 'Mittel (€€)',
      '€€€': 'Gehoben (€€€)',
      '€€€€': 'Premium (€€€€)'
    };
    chips.push({ key: 'priceLevel', label: priceLabels[filters.priceLevel] || filters.priceLevel, onRemoveKey: 'priceLevel' });
    activeCount++;
  }

  // Search Query
  const query = (filters.query || filters.searchQuery || '').trim();
  if (query) {
    chips.push({ key: 'search', label: `"${query}"`, onRemoveKey: 'search' });
    activeCount++;
  }

  // Location
  const locationLabel = filters.location?.label?.trim();
  if (locationLabel) {
    chips.push({ key: 'location', label: locationLabel, onRemoveKey: 'location' });
    activeCount++;
  }

  return { chips, activeCount };
};

export const useFilterStore = create<FilterState>()(
  subscribeWithSelector((set, get) => ({
    ...DEFAULT_FILTERS,
    
    // Aktionen
    setQuery: (query) => set({ query, searchQuery: query }), // Sync beide
    setSearchQuery: (query) => set({ query, searchQuery: query }), // Alias
    setLocation: (location) => set(() => {
      if (location === null) return { location: null };
      if (typeof location === 'string') {
        const label = location.trim();
        return { location: label ? { label } : null };
      }
      return { location };
    }),
    setLocationLabel: (label) => set((state) => {
      const trimmed = label.trim();
      if (!trimmed) return { location: null };
      const current = state.location || {};
      return {
        location: {
          ...current,
          label: trimmed
        }
      };
    }),
    setCoordinates: (lat, lng) => set((state) => {
      const current = state.location || { label: '' };
      return {
        location: {
          ...current,
          lat,
          lng
        }
      };
    }),
    // P0 Fix: Duplicate Filter Chips - ensure unique arrays
    setCuisines: (cuisineIds) => set({ cuisineIds: Array.from(new Set(cuisineIds)) }),
    setDistance: (distance) => set({ distance }),
    setSkills: (skills) => set({ skills: Array.from(new Set(skills)) }),
    setOpenNow: (openNow) => set({ openNow }),
    setDeliveryOnly: (deliveryOnly) => set({ deliveryOnly }),
    // Wenn deliveryMode auf 'delivery' gesetzt wird, deliveryOnly zurücksetzen (redundant)
    setDeliveryMode: (deliveryMode) => set({ 
      deliveryMode,
      ...(deliveryMode === 'delivery' ? { deliveryOnly: false } : {})
    }),
    setPriceLevel: (priceLevel) => set({ priceLevel }),
    setSortOption: (sortOption) => set({ sortOption }),
    
    // P0 Fix: Reset All - einzige Reset-Funktion, setzt ALLES auf Baseline (ohne zeitabhängige Defaults)
    resetAll: () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[FilterStore] Before resetAll:', get());
      }
      set({
        ...DEFAULT_FILTERS_BASELINE, // Verwende konstante Baseline statt zeitabhängige Defaults
        location: null
      });
      setTimeout(() => {
        if (process.env.NODE_ENV === 'development') {
          console.log('[FilterStore] After resetAll:', get());
        }
      }, 0);
    },
    
    // Alias für resetAll (Kompatibilität)
    resetFilters: () => {
      get().resetAll();
    },
    
    // P0 Fix: Has Active Filters - verwendet getActiveFilters mit Baseline
    hasActiveFilters: () => {
      const state = get();
      const { activeCount } = getActiveFilters(state, DEFAULT_FILTERS_BASELINE);
      return activeCount > 0;
    },
    
    // P0 Fix: Get Active Filters Count - verwendet getActiveFilters Helper mit Baseline
    getActiveFiltersCount: () => {
      const state = get();
      const { activeCount } = getActiveFilters(state, DEFAULT_FILTERS_BASELINE);
      return activeCount;
    }
  }))
);